import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";
import deepResearchStore from "Stores/deepResearchData";
import Utils from "Utils/utils";

const PAGE_SIZE = 50;

const formatCredits = (credits) => {
  const n = Number(credits || 0);
  if (n === 0) return "0 credits";
  return `-${n} credit${n === 1 ? "" : "s"}`;
};

const ownerFromRow = (row) => ({
  firstName: row.ownerFirstName || "",
  lastName: row.ownerLastName || "",
  email: row.ownerEmail || ""
});

const OwnerAvatar = ({ row }) => {
  const owner = ownerFromRow(row);
  const initial = Utils.getOwnerInitial(owner) || "—";
  const name = Utils.getOwnerName(owner) || owner.email || "Unknown user";
  const [showTip, setShowTip] = useState(false);
  return (
    <span
      className="drUsageOwnerCell"
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <span className="circleOwner">{initial}</span>
      {showTip && <span className="drUsageOwnerTooltip">{name}</span>}
    </span>
  );
};

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "—";
    return (
      d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) +
      ", " +
      d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit"
      })
    );
  } catch (e) {
    return "—";
  }
};

// Display-friendly LinkedIn URL — strip the protocol/www, keep the rest.
const prettyUrl = (raw) => {
  if (!raw) return "—";
  try {
    return raw.replace(/^https?:\/\//, "").replace(/^www\./, "");
  } catch (e) {
    return raw;
  }
};

const sellerProfileLabel = (row, profilesById) => {
  if (row.sellerProfileName) return row.sellerProfileName;
  const id = row.sellerProfileId;
  if (id && profilesById[id]) {
    const p = profilesById[id];
    return p.name || p.company_name || p.product_name || "Untitled profile";
  }
  return "—";
};

const cacheLabel = (status) => {
  if (!status) return "—";
  switch (status) {
    case "FULL_HIT":
      return "Cache hit";
    case "RESEARCH_CACHED":
      return "Research cached";
    case "FRESH":
      return "Fresh";
    default:
      return status;
  }
};

const StatusPill = ({ success, errorMessage }) => {
  if (success) {
    return <span className="drUsageStatusPill ok">Success</span>;
  }
  return (
    <span className="drUsageStatusPill fail" title={errorMessage || "Failed"}>
      Failed
    </span>
  );
};

const Loader = () => (
  <div className="drUsageLoader">
    {Array.from({ length: 6 }, (_, i) => (
      <div className="drUsageLoaderRow" key={i}>
        {Array.from({ length: 8 }, (__, j) => (
          <span className="drUsageLoaderCell linear-background" key={j} />
        ))}
      </div>
    ))}
  </div>
);

const DeepResearchUsage = observer(() => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Load seller profiles once so we can resolve sellerProfileId → name when the
  // log row was written before the denormalized name field was added.
  useEffect(() => {
    deepResearchStore.fetchSellerProfiles({ force: false });
  }, []);

  const fetchPage = (nextPage) => {
    setLoading(true);
    setError("");
    makeApi({
      url: `${URLS.deepResearchListUsage}?page=${nextPage}&size=${PAGE_SIZE}`,
      method: "GET"
    })
      .then((res) => {
        const data = res?.data || {};
        setRows(Array.isArray(data.rows) ? data.rows : []);
        setPage(typeof data.page === "number" ? data.page : nextPage);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load usage data.");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profilesById = (deepResearchStore.sellerProfiles || []).reduce(
    (acc, p) => {
      if (p?.id) acc[p.id] = p;
      return acc;
    },
    {}
  );

  const isEmpty = !loading && !error && rows.length === 0;

  return (
    <section className="drUsageWrapper">
      <div className="drUsageCont">
        <div className="headerCont">
          <div className="headerTitle">Deep Research Usage</div>
          <p className="headerSubtitle">
            A log of every Deep Research run your team has made, recent first.
            See who ran it, who it researched, what it cost in credits, and
            whether it succeeded.
          </p>
        </div>

        {error && <div className="drUsageError">{error}</div>}

        {loading ? (
          <Loader />
        ) : isEmpty ? (
          <div className="drUsageEmpty">
            <span className="drUsageEmptyIcon" aria-hidden>
              <i className="material-icons-outlined drIcon" style={{fontSize: "44px"}}>biotech</i>
            </span>
            <div className="drUsageEmptyTitle">Your team hasn't run Deep Research yet</div>
            <p>
              Kick it off from any contact, or add a Deep Research step to a
              sequence. As soon as the first run completes, you'll see the
              date, who ran it, the contact, the seller profile used, the
              credits charged, and whether it succeeded — all in this table.
            </p>
            <div className="drUsageEmptyHint">
              Each run uses <strong>40 credits</strong> on Instant mode or{" "}
              <strong>20 credits</strong> on Saver. Recently researched contacts
              are reused for free.
            </div>
          </div>
        ) : (
          <>
            <div className="drUsageTableWrap">
              <table className="drUsageTable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Owner</th>
                    <th>Contact</th>
                    <th>Sequence</th>
                    <th>Seller profile</th>
                    <th>Source</th>
                    <th className="drUsageColRight">Credits</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="drUsageColMuted">
                        {formatDate(row.createdAt)}
                      </td>
                      <td>
                        <OwnerAvatar row={row} />
                      </td>
                      <td>
                        <div className="drUsageContactCell">
                          {row.contactId ? (
                            <a
                              className="drUsageContactLink"
                              href={`/contacts/${row.contactId}/deepresearch`}
                              title={
                                row.contactName
                                  ? `${row.contactName} — open in Salesgear`
                                  : "Open contact in Salesgear"
                              }
                            >
                              {row.contactName || "Open contact"}
                            </a>
                          ) : (
                            <a
                              className="drUsageContactLink"
                              href={
                                row.normalizedUrl?.startsWith("http")
                                  ? row.normalizedUrl
                                  : `https://${row.normalizedUrl}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              title={row.normalizedUrl}
                            >
                              {prettyUrl(row.normalizedUrl)}
                            </a>
                          )}
                          <a
                            className="drUsageSubLink"
                            href={
                              row.normalizedUrl?.startsWith("http")
                                ? row.normalizedUrl
                                : `https://${row.normalizedUrl}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            title={row.normalizedUrl}
                          >
                            {prettyUrl(row.normalizedUrl)}
                          </a>
                        </div>
                      </td>
                      <td>
                        {row.sequenceId ? (
                          <a
                            className="drUsageSequenceLink"
                            href={`/sequences/all/${row.sequenceId}`}
                            title={row.sequenceName || "Open sequence"}
                          >
                            {row.sequenceName || "Open sequence →"}
                          </a>
                        ) : (
                          <span className="drUsageColMuted">Ad-hoc</span>
                        )}
                      </td>
                      <td>{sellerProfileLabel(row, profilesById)}</td>
                      <td className="drUsageColMuted">
                        {row.source || "—"}
                      </td>
                      <td className="drUsageColRight">
                        {formatCredits(row.creditsUsed)}
                      </td>
                      <td>
                        <StatusPill
                          success={row.success}
                          errorMessage={row.errorMessage}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="drUsagePager">
              <div className="drUsagePagerInfo">
                Showing {rows.length} of {totalElements} runs
              </div>
              <div className="drUsagePagerControls">
                <button
                  type="button"
                  className="drUsagePagerBtn"
                  disabled={page === 0}
                  onClick={() => fetchPage(page - 1)}
                >
                  ← Previous
                </button>
                <span className="drUsagePagerPage">
                  Page {page + 1} of {Math.max(totalPages, 1)}
                </span>
                <button
                  type="button"
                  className="drUsagePagerBtn"
                  disabled={page + 1 >= totalPages}
                  onClick={() => fetchPage(page + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
});

export default DeepResearchUsage;
